"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FundNavSeries } from "@/type/comparePageTypes";
import { normalizeNavMap, toTime, updateSeriesVisibility } from "@/utils/comparePageFunctions";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import Image from "next/image";
import { RollingFund, RollingReturnResponse } from "@/type/rollingPageTypes";
import { buildChartData } from "@/utils/rollingReturnsCalculation";

type CompareFundsNavChartProps = {
  apiResult: RollingReturnResponse[];
  mappedFunds: RollingFund[];
  benchmark?: FundNavSeries | null;
  timePeriod?: string;
};

export default function RollingReturnCompareNav({
  timePeriod,
  apiResult,
  mappedFunds,
  benchmark,
}: CompareFundsNavChartProps) {
  const [isChartLoading, setIsChartLoading] = useState(true);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const funds = useMemo(() => {
    if (!apiResult.length) {
      return;
    }

    return benchmark ? buildChartData(apiResult, mappedFunds, benchmark) : buildChartData(apiResult, mappedFunds);
  }, [apiResult, mappedFunds, benchmark]);

  const formatRange = (startTime: number, years: number) => {
    const start = new Date(startTime);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + years);

    const formatFull = (date: Date): string =>
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });

    const formatShort = (date: Date): string =>
      date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });

    return {
      full: `${formatFull(start)} - ${formatFull(end)}`,
      short: `${formatShort(start)}\nto\n${formatShort(end)}`,
    };
  };

  // setting the series
  const series = useMemo<Highcharts.SeriesLineOptions[]>(() => {
    if (!funds) return [];
    return funds.map((fund): Highcharts.SeriesLineOptions => {
      const navData = normalizeNavMap(fund.data);

      if (navData.length == 0) {
        return {
          type: "line",
          name: fund.name,
          color: fund.color,
          data: [],
        };
      }

      return {
        type: "line",
        name: fund.name,
        color: fund.color,
        lineWidth: 2,
        marker: { enabled: false },
        data: navData.map((d) => [toTime(d.date), d.nav]),
      };
    });
  }, [funds]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    requestAnimationFrame(() => {
      updateSeriesVisibility(chart);
    });
  }, [series]);

  const options: Highcharts.Options = {
    chart: {
      height: 380,
      backgroundColor: "transparent",
      zooming: {
        type: undefined, // ✅ disables zoom
        mouseWheel: {
          enabled: false, // ✅ disables mouse wheel zoom (v11+)
        },
      },
      panning: {
        enabled: false, // ✅ disables drag panning
      },
      panKey: undefined,
      events: {
        load: function (this: Highcharts.Chart) {
          setIsChartLoading(false);
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "datetime",
      tickLength: 0,
      lineColor: "#E5E7EB",
      gridLineWidth: 1, // ✅ show grid lines
      gridLineColor: "#E5E7EB", // optional styling
      gridLineDashStyle: "Dash",
      labels: {
        useHTML: true,
        formatter: function () {
          const years = Number(timePeriod);
          const range = formatRange(this.value as number, years);

          return `
        <div style="text-align:center; line-height:1.2;">
          ${range.short.replace(/\n/g, "<br/>")}
        </div>
      `;
        },
      },
    },
    yAxis: {
      title: { text: "" },
      // opposite: false,
      // gridLineWidth: 1,
      // gridLineColor: "#E5E7EB",
      // labels: {
      //   enabled: true,
      //   align: "right",
      //   x: -5,
      //   formatter: function () {
      //     return `${this.value}%`;
      //   },
      //   style: {
      //     fontSize: "12px",
      //     color: "#6B7280",
      //   },
      // },
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      width: "100%",
      itemWidth: 280,
      symbolRadius: 8,
      itemMarginBottom: 8,
      itemStyle: {
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        opacity: 1,
      },
      labelFormatter: function () {
        return `<div style="color:${this.color}">
                  <span class="line-clamp-1 max-w-36"> ${this.name}</span> <br/> 
                </div>`;
      },
      navigation: {
        enabled: false,
      },
      itemHiddenStyle: {
        textDecoration: "none",
        opacity: 0.5,
      },
    },
    plotOptions: {
      series: {
        animation: true,
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function () {
        const years = Number(timePeriod);
        const range = formatRange(this.x as number, years);

        let html = `<div class="text-center">
                  <div class="font-medium mb-1">${range.full}</div>`;

        this.points?.forEach((point) => {
          const value = point.y ?? 0;
          const sign = value >= 0 ? "+" : "";

          html += `
        <div style="color:${point.series.color}">
          ● ${point.series.name}: <b>${sign}${value.toFixed(2)}%</b>
        </div>
      `;
        });

        html += `</div>`;
        return html;
      },
    },
    series,
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 768,
          },
          chartOptions: {
            chart: {
              height: 400,
            },
            legend: {
              itemWidth: 220,
              itemStyle: {
                fontSize: "12px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "11px",
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 431,
          },
          chartOptions: {
            legend: {
              layout: "vertical",
              align: "center",
              itemWidth: undefined,
              itemStyle: {
                fontSize: "11px",
              },
            },
            xAxis: {
              labels: {
                style: {
                  fontSize: "10px",
                },
              },
            },
            plotOptions: {
              series: {
                lineWidth: 2,
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="w-full rounded-3xl border border-gray-200 bg-white xs:p-2.5">
      {isChartLoading && (
        <div className="flex items-center justify-center h-400 sm:h-95">
          <Image
            src="/next_assets/images/gif/chart_loading.gif"
            alt="Loading Chart Animation"
            unoptimized
            loading="eager"
            width={256}
            height={144}
            priority
            className="w-64 h-36 mx-auto"
          />
        </div>
      )}

      <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="stockChart" options={options} />
    </div>
  );
}
